import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import DatePicker from 'react-datepicker';

import { Input } from './Input';
import { Icon } from '../../ui_elements/icons';
import customIcons from '../../styles/icons';
import { colors, fonts, zindex, leading } from '../../styles/theme';

const dateTimeStyles = css`
  position: relative;
  text-align: center;
  letter-spacing: 0;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker {
    background-color: ${colors.white};
    color: ${colors.black};
    border: 1px solid ${colors.grey.secondaryDark};
    box-shadow: 0 0 1px 0 ${colors.grey.secondaryDark};
    border-radius: 5px;
    display: inline-block;
    position: relative;
    @media screen and (max-width: 480px) {
      display: flex;
      flex-direction: column;
      .react-datepicker__time-container {
        width: 100%;
        border-left: 0;
        border-top: 1px solid ${colors.grey.secondaryDark};
        .react-datepicker__time {
          .react-datepicker__time-box {
            width: auto;
          }
        }
      }
    }
  }

  .react-datepicker__triangle {
    position: absolute;
    left: 50px;
  }

  .react-datepicker-popper {
    z-index: 1;

    &[data-placement^='bottom'] {
      margin-top: 10px;
      .react-datepicker__triangle {
        width: 1px;
        border: 10px solid transparent;
        border-top: none;
        border-bottom-color: ${colors.grey.secondaryDark};
        margin-top: -10px;
        &::before {
          content: '';
          border: 8px solid transparent;
          border-top: none;
          border-bottom-color: ${colors.grey.secondaryLight};
          height: 0;
          position: absolute;
          top: 2px;
          left: -8px;
        }
      }
    }

    &[data-placement^='top'] {
      margin-bottom: 10px;

      .react-datepicker__triangle {
        width: 1px;
        border: 10px solid transparent;
        border-bottom: none;
        border-top-color: ${colors.grey.secondaryDark};
        bottom: -10px;
        &::before {
          content: '';
          border: 8px solid transparent;
          border-bottom: none;
          border-top-color: ${colors.white};
          height: 0;
          position: absolute;
          bottom: 2px;
          left: -8px;
        }
      }
    }
  }

  .react-datepicker__header {
    text-align: center;
    background-color: ${colors.grey.secondaryLight};
    border-bottom: 1px solid ${colors.grey.secondary};
    border-top-left-radius: 5px;
    border-top-right-radius: 0;
    padding-top: 10px;
    position: relative;
    @media screen and (max-width: 480px) {
      border-top-right-radius: 5px;
    }
  }

  .react-datepicker__header--time {
    padding-bottom: 8px;
    padding-left: 5px;
    padding-right: 5px;
    border-top-right-radius: 5px;
    border-top-left-radius: 0;
    @media screen and (max-width: 480px) {
      padding: 5px;
      border-radius: 0;
    }
  }

  .react-datepicker__year-dropdown-container--select,
  .react-datepicker__month-dropdown-container--select,
  .react-datepicker__month-year-dropdown-container--select,
  .react-datepicker__year-dropdown-container--scroll,
  .react-datepicker__month-dropdown-container--scroll,
  .react-datepicker__month-year-dropdown-container--scroll {
    display: inline-block;
    margin: 0 2px;
  }

  .react-datepicker__current-month,
  .react-datepicker-time__header {
    text-transform: uppercase;
    font-size: ${fonts.sans.size.normal};
    font-weight: ${fonts.sans.weight.bold};
    line-height: ${leading.full};
  }

  .react-datepicker-time__header {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .react-datepicker__navigation {
    background: none;
    border: 0;
    height: 20px;
    line-height: 20px;
    outline: 0;
    overflow: hidden;
    padding: 0;
    position: absolute;
    text-align: center;
    text-indent: -9999em;
    top: 10px;
    width: 15px;
    z-index: 1;
    &::before {
      ${fonts.icons};
      display: block;
      text-indent: 0;
      font-size: 20px;
    }
    &:hover {
      cursor: pointer;
      color: ${colors.blue.primary};
    }

    &.react-datepicker__navigation--previous {
      left: 10px;
      &::before {
        content: '${customIcons['angle-left']}';
      }
    }

    &.react-datepicker__navigation--previous--disabled,
    &.react-datepicker__navigation--previous--disabled:hover {
      border-right-color: ${colors.grey.secondary};
      cursor: not-allowed;
    }

    &.react-datepicker__navigation--next {
      right: 10px;
      &::before {
        content: '${customIcons['angle-right']}';
      }
    }

    &.react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) {
      right: 100px;
      @media screen and (max-width: 480px) {
        right: 10px;
      }
    }

    &.react-datepicker__navigation--next--disabled,
    &.react-datepicker__navigation--next--disabled:hover {
      border-left-color: ${colors.grey.secondary};
      cursor: not-allowed;
    }
  }

  .react-datepicker__month-container {
    float: left;
  }

  .react-datepicker__month {
    margin: 5px;
    text-align: center;
  }

  .react-datepicker__input-time-container {
    clear: both;
    width: 100%;
    float: left;
    margin: 5px 0 10px 15px;
    text-align: left;
    .react-datepicker-time__caption {
      display: inline-block;
    }
    .react-datepicker-time__input-container {
      display: inline-block;
      .react-datepicker-time__input {
        display: inline-block;
        margin-left: 10px;
        input {
          width: 85px;
        }
        input[type='time']::-webkit-inner-spin-button,
        input[type='time']::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type='time'] {
          -moz-appearance: textfield;
        }
      }
      .react-datepicker-time__delimiter {
        margin-left: 5px;
        display: inline-block;
      }
    }
  }

  .react-datepicker__time-container {
    float: right;
    border-left: 1px solid ${colors.grey.secondaryDark};
    width: 90px;

    .react-datepicker__time-container--with-today-button {
      display: inline;
      border: 1px solid ${colors.blue.primary};
      border-radius: 0.3rem;
      position: absolute;
      right: -72px;
      top: 0;
    }

    .react-datepicker__time {
      position: relative;

      .react-datepicker__time-box {
        overflow-x: hidden;
        margin: 0 auto;
        text-align: center;
        font-family: 'helvetica', sans-serif;
        font-size: ${fonts.sans.size.small};
        ul.react-datepicker__time-list {
          list-style: none;
          margin: 0;
          height: 30px;
          overflow-y: scroll;
          padding-right: 0px;
          padding-left: 0px;
          width: 100%;
          box-sizing: content-box;

          li.react-datepicker__time-list-item {
            height: 30px;
            padding: 3px 5px;
            line-height: 24px;
            white-space: nowrap;
            &:hover {
              cursor: pointer;
              background-color: ${colors.grey.secondary};
            }
          }

          li.react-datepicker__time-list-item--selected {
            background-color: ${colors.blue.primaryLight};
            color: ${colors.white};
            font-weight: ${fonts.sans.weight.bold};
            &:hover {
              background-color: ${colors.blue.primary};
            }
          }

          li.react-datepicker__time-list-item--disabled {
            color: ${colors.grey.secondaryDark};
            background: none;
            &:hover {
              cursor: not-allowed;
              background: none;
            }
          }
        }
      }
    }
  }

  .react-datepicker__day-names {
    margin: 7px 0 2px;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    white-space: nowrap;
  }

  .react-datepicker__day-name,
  .react-datepicker__day,
  .react-datepicker__time-name {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    border: 2px solid transparent;
    font-size: 11px;
    font-weight: ${fonts.sans.weight.normal};
  }

  .react-datepicker__day {
    height: 28px;
    border-radius: 50%;
    font-size: ${fonts.sans.size.small};

    &:hover {
      cursor: pointer;
      background-color: ${colors.grey.secondary};
    }
  }

  .react-datepicker__day--today {
    font-weight: ${fonts.sans.weight.bold};
  }

  .react-datepicker__day--highlighted {
    background-color: ${colors.blue.primaryDark};
    color: ${colors.white};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range {
    background-color: ${colors.blue.primaryLight};
    color: ${colors.white};
    border-color: ${colors.blue.primaryLight};
    border-radius: 0;

    &:hover {
      cursor: pointer;
      background-color: ${colors.blue.primary};
      border-color: ${colors.blue.primary};
      color: ${colors.white};
    }
  }

  .react-datepicker__day--keyboard-selected {
    background-color: ${colors.blue.primaryLight};
    color: ${colors.white};

    &:hover {
      cursor: default;
      background-color: ${colors.blue.primaryLight};
    }
  }

  .react-datepicker__day--disabled {
    color: ${colors.grey.secondaryDark};
    &:hover {
      cursor: not-allowed;
      background-color: transparent;
    }
  }

  .react-datepicker__input-container {
    position: relative;
    z-index: ${zindex.component.base};
    &:after {
      ${fonts.icons};
      content: '${customIcons['angle-down']}';
      font-size: ${fonts.sans.size.large};
      width: 20px;
      position: absolute;
      right: 10px;
      top: 0;
      bottom: 0;
      line-height: 40px;
    }
  }

  ${Input} {
    padding-left: 35px;
    font-size: ${fonts.sans.size.normal};
  }
`;

const CalendarIcon = styled(Icon)`
  position: absolute;
  width: 40px;
  line-height: 40px;
  left: 0;
  top: 0;
  z-index: ${zindex.component.aboveBase};
  color: ${colors.blue.primaryDark};
`;

export const DateTimeWrapper = styled('div')`
  ${dateTimeStyles};
  display: ${(props) => (props.range ? 'inline-block' : 'block')};
`;

export class DateTimePicker extends PureComponent {
  render() {
    const {
      timeFormat,
      timeIntervals,
      dateFormat,
      placeholder,
      showTimeSelect,
      popperPlacement,
      selectsStart,
      selectsEnd,
      startDate,
      endDate,
      minDate,
      range,
    } = this.props;
    return (
      <DateTimeWrapper range={range}>
        <CalendarIcon type="calendar" />
        <DatePicker
          {...this.props}
          popperPlacement={popperPlacement}
          placeholderText={placeholder}
          customInput={<Input />}
          showTimeSelect={showTimeSelect}
          timeFormat={timeFormat}
          timeIntervals={timeIntervals}
          dateFormat={dateFormat}
          nextMonthButtonLabel=""
          previousMonthButtonLabel=""
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          startDate={startDate}
          endDate={endDate}
          minDate={minDate}
        />
      </DateTimeWrapper>
    );
  }
}

DateTimePicker.propTypes = {
  timeFormat: PropTypes.string,
  timeIntervals: PropTypes.number,
  dateFormat: PropTypes.string,
  placeholder: PropTypes.string,
  showTimeSelect: PropTypes.bool,
  popperPlacement: PropTypes.string,
  selectsStart: PropTypes.bool,
  selectsEnd: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  range: PropTypes.bool,
};

DateTimePicker.defaultProps = {
  timeFormat: 'hh:mm a',
  timeIntervals: 15,
  dateFormat: 'MMMM d, yyyy h:mm aa',
  placeholder: '',
  showTimeSelect: false,
  popperPlacement: 'top-start',
  selectsStart: false,
  selectsEnd: false,
  startDate: null,
  endDate: null,
  minDate: null,
  range: false,
};
